/**
 * Notificações / Infrações (Portal do Condômino, Fase 3, 0.1.34). Mobile-first.
 *
 * Morador: vê SÓ as notificações da própria unidade (privacidade garantida no
 * backend) e pode abrir a carta em PDF. Gestão (síndico/Dono): cadastra os TIPOS
 * de infração do condomínio, registra ocorrências por unidade, filtra por
 * unidade/status, edita/exclui e gera a carta de notificação (PDF).
 * 0.1.88: fotos de evidência — o síndico anexa (na criação ou depois); elas saem na
 * carta e a unidade notificada as vê.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, FileText, Gavel, Loader2, Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import { api, ApiError, cartaInfracaoUrl, type FotoAnexo } from "../lib/api";
import { EscolherFotos, FotosEvidencia, enviarFotos } from "./FotosEvidencia";

interface Notif {
  id: number; unidade: string; descricao: string; base_legal: string | null;
  data: string | null; status: string; valor: number | null; observacao: string | null;
  registrado_por: string | null; criado_em: string | null; fotos?: FotoAnexo[];
}
interface NotifResp {
  condominio_id: number; itens: Notif[]; pode_gerir: boolean;
  unidades: string[]; status_possiveis: string[];
}
interface Tipo { id: number; descricao: string; base_legal: string | null; valor_multa_padrao: number | null; ativo: boolean }

const STATUS_LABEL: Record<string, string> = {
  notificado: "Notificado", multado: "Multado", pago: "Pago", recorrido: "Recorrido", arquivado: "Arquivado",
};
const STATUS_COR: Record<string, string> = {
  notificado: "bg-amber-50 text-amber-700", multado: "bg-rose-50 text-rose-700",
  pago: "bg-emerald-50 text-emerald-700", recorrido: "bg-indigo-50 text-indigo-700",
  arquivado: "bg-slate-100 text-slate-500",
};
const fmtData = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const brl = (v: number | null) =>
  v == null ? null : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface NForm {
  id: number | null; unidade: string; tipo_infracao_id: string; descricao: string;
  base_legal: string; data: string; valor: string; status: string; observacao: string;
}
const N_VAZIO: NForm = {
  id: null, unidade: "", tipo_infracao_id: "", descricao: "", base_legal: "",
  data: "", valor: "", status: "notificado", observacao: "",
};

export function NotificacoesPage({
  condominioId, onVoltar, gestao = true,
}: {
  condominioId: number;
  onVoltar: () => void;
  gestao?: boolean;   // false = aberto pelo Portal (só consulta; morador vê só a sua unidade)
}) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["notificacoes", condominioId] });

  const [fUnidade, setFUnidade] = useState("");
  const [fStatus, setFStatus] = useState("");

  const { data, isLoading } = useQuery<NotifResp>({
    queryKey: ["notificacoes", condominioId, fUnidade, fStatus],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (fUnidade) qs.set("unidade", fUnidade);
      if (fStatus) qs.set("status_f", fStatus);
      const s = qs.toString();
      return api.get(`/condominios/${condominioId}/notificacoes${s ? `?${s}` : ""}`);
    },
  });
  // No Portal a tela é só de consulta; a gestão fica no menu inicial do condomínio.
  const podeGerir = gestao && (data?.pode_gerir ?? false);

  // --- Tipos de infração (só gestão) ---
  const [verTipos, setVerTipos] = useState(false);
  const { data: tipos } = useQuery<Tipo[]>({
    queryKey: ["tipos-infracao", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/tipos-infracao`),
    enabled: podeGerir,
  });
  const [tForm, setTForm] = useState<{ id: number | null; descricao: string; base_legal: string; valor: string } | null>(null);
  const salvarTipo = useMutation({
    mutationFn: () => {
      const t = tForm!;
      const corpo = { descricao: t.descricao.trim(), base_legal: t.base_legal.trim() || null,
        valor_multa_padrao: t.valor ? Number(t.valor) : null };
      return t.id ? api.patch(`/tipos-infracao/${t.id}`, corpo)
        : api.post(`/condominios/${condominioId}/tipos-infracao`, corpo);
    },
    onSuccess: () => { setTForm(null); qc.invalidateQueries({ queryKey: ["tipos-infracao", condominioId] }); },
  });
  const excluirTipo = useMutation({
    mutationFn: (id: number) => api.delete(`/tipos-infracao/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tipos-infracao", condominioId] }),
  });
  const criarPadroes = useMutation({
    mutationFn: () => api.post(`/condominios/${condominioId}/tipos-infracao/padroes`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tipos-infracao", condominioId] }),
  });

  // --- Notificações (ocorrências) ---
  const [form, setForm] = useState<NForm | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [fotosNovas, setFotosNovas] = useState<File[]>([]);
  const salvar = useMutation({
    mutationFn: async () => {
      const f = form!;
      const corpo = {
        unidade: f.unidade.trim(), tipo_infracao_id: f.tipo_infracao_id ? Number(f.tipo_infracao_id) : null,
        descricao: f.descricao.trim() || null, base_legal: f.base_legal.trim() || null,
        data: f.data || null, valor: f.valor ? Number(f.valor) : null,
        status: f.status, observacao: f.observacao.trim() || null,
      };
      const n = await (f.id ? api.patch<Notif>(`/notificacoes/${f.id}`, corpo)
        : api.post<Notif>(`/condominios/${condominioId}/notificacoes`, corpo));
      if (fotosNovas.length) {
        try { await enviarFotos("notificacoes", n.id, fotosNovas); }
        catch (e) {
          throw new Error("Notificação salva, mas a foto não foi enviada: "
            + (e instanceof ApiError ? e.message : "anexe de novo pelo cartão da notificação."));
        }
      }
      return n;
    },
    onSuccess: () => { setForm(null); setErro(null); setFotosNovas([]); inval(); },
    onError: (e) => { setErro(e instanceof Error ? e.message : "Não foi possível salvar."); inval(); },
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/notificacoes/${id}`),
    onSuccess: () => inval(),
  });

  // Ao escolher um tipo no formulário, sugere descrição/base/valor.
  const escolherTipo = (tipoId: string) => {
    const t = (tipos ?? []).find((x) => String(x.id) === tipoId);
    setForm((prev) => prev && ({
      ...prev, tipo_infracao_id: tipoId,
      descricao: prev.descricao || (t?.descricao ?? ""),
      base_legal: prev.base_legal || (t?.base_legal ?? ""),
      valor: prev.valor || (t?.valor_multa_padrao != null ? String(t.valor_multa_padrao) : ""),
    }));
  };

  const itens = data?.itens ?? [];

  return (
    <div className="max-w-2xl mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold">
          <Gavel size={18} /> Notificações
        </div>
      </div>

      {!podeGerir && (
        <p className="text-xs text-slate-500 mb-3">Aqui aparecem as notificações da sua unidade.</p>
      )}

      {/* GESTÃO: tipos de infração + registrar */}
      {podeGerir && (
        <div className="mb-3 space-y-2">
          <button onClick={() => setVerTipos((v) => !v)}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50">
            <Settings2 size={16} /> {verTipos ? "Ocultar" : "Gerenciar"} tipos de infração
          </button>

          {verTipos && (
            <div className="p-3 rounded-xl border border-slate-200 bg-slate-50/60 space-y-2">
              {(tipos ?? []).map((t) => (
                <div key={t.id} className="flex items-start justify-between gap-2 bg-white rounded-md border border-slate-200 px-3 py-2">
                  <div className="min-w-0 text-sm">
                    <div className="font-medium text-slate-800">{t.descricao}</div>
                    <div className="text-xs text-slate-500">{t.base_legal || "sem base legal"}{t.valor_multa_padrao != null && ` · ${brl(t.valor_multa_padrao)}`}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setTForm({ id: t.id, descricao: t.descricao, base_legal: t.base_legal ?? "", valor: t.valor_multa_padrao != null ? String(t.valor_multa_padrao) : "" })}
                      className="p-1 rounded text-slate-500 hover:bg-slate-100"><Pencil size={14} /></button>
                    <button onClick={() => { if (confirm("Excluir este tipo?")) excluirTipo.mutate(t.id); }}
                      className="p-1 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => criarPadroes.mutate()} disabled={criarPadroes.isPending}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-teal-300 bg-teal-50 text-teal-800 text-sm hover:bg-teal-100 disabled:opacity-50">
                <Plus size={14} /> {criarPadroes.isPending
                  ? "Criando…"
                  : (tipos ?? []).length === 0
                    ? "Criar tipos padrão de mercado"
                    : "Adicionar tipos padrão de mercado (não duplica)"}
              </button>
              {tForm ? (
                <div className="bg-white rounded-md border border-slate-200 p-2.5 space-y-2">
                  <input value={tForm.descricao} onChange={(e) => setTForm({ ...tForm, descricao: e.target.value })}
                    placeholder="Descrição (ex.: Barulho após 22h)" className="w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={tForm.base_legal} onChange={(e) => setTForm({ ...tForm, base_legal: e.target.value })}
                      placeholder="Base legal (Art. …)" className="text-sm rounded border border-slate-300 px-2 py-1.5" />
                    <input value={tForm.valor} onChange={(e) => setTForm({ ...tForm, valor: e.target.value })}
                      inputMode="decimal" placeholder="Multa padrão (R$)" className="text-sm rounded border border-slate-300 px-2 py-1.5" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => salvarTipo.mutate()} disabled={!tForm.descricao.trim()}
                      className="px-3 py-1.5 rounded bg-teal-600 hover:bg-teal-700 text-white text-sm disabled:opacity-50">Salvar tipo</button>
                    <button onClick={() => setTForm(null)} className="px-3 py-1.5 rounded border border-slate-300 text-slate-600 text-sm">Cancelar</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setTForm({ id: null, descricao: "", base_legal: "", valor: "" })}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-dashed border-slate-300 text-slate-500 text-sm hover:bg-white">
                  <Plus size={14} /> Novo tipo
                </button>
              )}
            </div>
          )}

          {!form && (
            <button onClick={() => { setForm({ ...N_VAZIO }); setErro(null); }}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-medium">
              <Plus size={18} /> Registrar notificação
            </button>
          )}
        </div>
      )}

      {/* GESTÃO: formulário de notificação */}
      {podeGerir && form && (
        <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-white space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })}
              className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
              <option value="">Unidade…</option>
              {(data?.unidades ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <select value={form.tipo_infracao_id} onChange={(e) => escolherTipo(e.target.value)}
              className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
              <option value="">Tipo de infração…</option>
              {(tipos ?? []).filter((t) => t.ativo).map((t) => <option key={t.id} value={t.id}>{t.descricao}</option>)}
            </select>
          </div>
          {(tipos ?? []).filter((t) => t.ativo).length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Nenhum tipo de infração cadastrado ainda. Use{" "}
              <button
                type="button"
                onClick={() => { setVerTipos(true); setTForm({ id: null, descricao: "", base_legal: "", valor: "" }); }}
                className="underline font-medium text-amber-800"
              >
                Gerenciar tipos de infração → Novo tipo
              </button>{" "}
              para criar os tipos do regimento deste condomínio. Você também pode registrar preenchendo a descrição e a base legal abaixo, sem escolher um tipo.
            </p>
          )}
          <input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            placeholder="Descrição da infração" className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          <input value={form.base_legal} onChange={(e) => setForm({ ...form, base_legal: e.target.value })}
            placeholder="Base legal (artigo do regimento/lei)" className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Data
              <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5" />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Multa (R$)
              <input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })}
                inputMode="decimal" className="text-sm rounded-md border border-slate-300 px-2 py-1.5" />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Status
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5 bg-white">
                {(data?.status_possiveis ?? []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}
              </select>
            </label>
          </div>
          <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
            placeholder="Observação (opcional)" rows={2} className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          {!form.id && <EscolherFotos arquivos={fotosNovas} onChange={setFotosNovas} />}
          {erro && <div className="text-sm text-rose-700">{erro}</div>}
          <div className="flex gap-2">
            <button onClick={() => salvar.mutate()} disabled={salvar.isPending || !form.unidade.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm disabled:opacity-50">
              {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Salvar
            </button>
            <button onClick={() => { setForm(null); setErro(null); setFotosNovas([]); }}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">Cancelar</button>
          </div>
        </div>
      )}

      {/* GESTÃO: filtros */}
      {podeGerir && itens.length > 0 && (
        <div className="flex gap-2 mb-3">
          <select value={fUnidade} onChange={(e) => setFUnidade(e.target.value)}
            className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 bg-white">
            <option value="">Todas as unidades</option>
            {(data?.unidades ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
            className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 bg-white">
            <option value="">Todos os status</option>
            {(data?.status_possiveis ?? []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}
          </select>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-14">
          <Loader2 size={18} className="animate-spin" /> Carregando…
        </div>
      ) : itens.length === 0 ? (
        <div className="text-center text-slate-400 py-14 text-sm">
          {podeGerir ? "Nenhuma notificação registrada." : "Você não tem notificações."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {itens.map((n) => (
            <div key={n.id} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800">{n.descricao}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Unidade {n.unidade} · {fmtData(n.data)}
                    {n.base_legal && <span> · {n.base_legal}</span>}
                  </div>
                  {n.valor != null && <div className="text-sm text-slate-700 mt-0.5">Multa: {brl(n.valor)}</div>}
                  {n.observacao && <div className="text-sm text-slate-600 mt-1">{n.observacao}</div>}
                  <FotosEvidencia caminho="notificacoes" registroId={n.id} fotos={n.fotos ?? []}
                    podeEnviar={podeGerir} podeApagar={podeGerir} onMudou={inval} />
                  <div className="mt-1.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COR[n.status] ?? "bg-slate-100 text-slate-600"}`}>
                      {STATUS_LABEL[n.status] ?? n.status}
                    </span>
                  </div>
                  <a href={cartaInfracaoUrl(n.id)} target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-teal-700 hover:text-teal-900">
                    <FileText size={13} /> Ver carta (PDF)
                  </a>
                </div>
                {podeGerir && (
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button title="Editar" onClick={() => { setErro(null); setForm({
                      id: n.id, unidade: n.unidade, tipo_infracao_id: "", descricao: n.descricao,
                      base_legal: n.base_legal ?? "", data: n.data ?? "", valor: n.valor != null ? String(n.valor) : "",
                      status: n.status, observacao: n.observacao ?? "",
                    }); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                    <button title="Excluir" onClick={() => { if (confirm("Excluir esta notificação?")) excluir.mutate(n.id); }}
                      className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
